import { atlasRoutes } from "@/app/router/routes";
import { Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";
import styled from "@emotion/styled";
import { Card, Flex, Heading, Text, Box, Tabs, Table, Button, Link } from "@radix-ui/themes";
import { useQuery } from "@tanstack/react-query";
import { compact, keyBy, truncate } from "lodash-es";
import { DateTime } from "luxon";
import { FC, useMemo } from "react";
import { useNavigate, useParams } from "react-router";

interface AccountListItem {
  from_account_id: string;
  to_account_id: string;
  from_firestore_ids: string[];
  autogenerate_target_account: boolean;
  mark_as_force_upgraded: boolean;
}

interface UpgradeConfig {
  account_list: AccountListItem[];
  generate_unique_ids_for_imported_items: boolean;
}

export const BatchAccountUpgradeViewPage: FC = () => {
  const { batchAccountUpgradeId } = useParams<{ batchAccountUpgradeId: Uuid }>();
  const navigate = useNavigate();

  if (!batchAccountUpgradeId) {
    return <div>Invalid ID</div>;
  }

  // Fetch batch account upgrade details
  const upgradeResult = useQuery({
    queryKey: ["batch-account-upgrade", batchAccountUpgradeId],
    queryFn: () =>
      api.get("/internal/v1/v1-batch-account-upgrades/{v1_batch_account_upgrade_id}", {
        params: {
          path: {
            v1_batch_account_upgrade_id: batchAccountUpgradeId,
          },
        },
      }),
  });

  // Extract target account IDs from the upgrade configuration
  const targetAccountIds = useMemo(() => {
    if (!upgradeResult.data?.data?.config_json) return [];

    try {
      const config = upgradeResult.data.data.config_json as unknown as UpgradeConfig;
      if (!config?.account_list?.length) return [];

      return config.account_list.map(item => item.to_account_id).filter(Boolean);
    } catch (error) {
      console.error("Error extracting target account IDs:", error);
      return [];
    }
  }, [upgradeResult.data]);

  // Fetch account details for the target accounts using React Query
  const accountDetailsResult = useQuery({
    queryKey: ["batch-account-target-accounts", targetAccountIds],
    queryFn: async () => {
      if (!targetAccountIds.length) return [];

      const results = await Promise.all(
        targetAccountIds.map(async accountId => {
          const response = await api.get(`/internal/v1/accounts/{account_id}`, {
            params: {
              path: {
                account_id: accountId,
              },
            },
          });

          return response.data;
        })
      );

      return results;
    },
    // Only run this query when we have target account IDs
    enabled: targetAccountIds.length > 0,
  });

  // Fetch events
  const eventsResult = useQuery({
    queryKey: ["batch-account-upgrade-events", batchAccountUpgradeId],
    queryFn: () =>
      api.post("/internal/v1/v1-batch-account-upgrade-events/search", {
        body: {
          filter_by_v1_batch_account_upgrade_id: batchAccountUpgradeId,
        },
      }),
  });

  // Fetch gathered items
  const gatheredItemsResult = useQuery({
    queryKey: ["batch-account-upgrade-gathered-items", batchAccountUpgradeId],
    queryFn: () =>
      api.post("/internal/v1/v1-batch-account-upgrade-gathered-items/search", {
        body: {
          filter_by_v1_batch_account_upgrade_id: batchAccountUpgradeId,
        },
      }),
  });

  // Fetch gathered relationships
  const gatheredRelationshipsResult = useQuery({
    queryKey: ["batch-account-upgrade-gathered-relationships", batchAccountUpgradeId],
    queryFn: () =>
      api.post("/internal/v1/v1-batch-account-upgrade-gathered-relationships/search", {
        body: {
          filter_by_v1_batch_account_upgrade_id: batchAccountUpgradeId,
        },
      }),
  });

  // Fetch imported items
  const importedItemsResult = useQuery({
    queryKey: ["batch-account-upgrade-imported-items", batchAccountUpgradeId],
    queryFn: () =>
      api.post("/internal/v1/v1-batch-account-upgrade-imported-items/search", {
        body: {
          filter_by_v1_batch_account_upgrade_id: batchAccountUpgradeId,
        },
      }),
  });

  // Fetch imported relationships
  const importedRelationshipsResult = useQuery({
    queryKey: ["batch-account-upgrade-imported-relationships", batchAccountUpgradeId],
    queryFn: () =>
      api.post("/internal/v1/v1-batch-account-upgrade-imported-relationships/search", {
        body: {
          filter_by_v1_batch_account_upgrade_id: batchAccountUpgradeId,
        },
      }),
  });

  const upgrade = upgradeResult.data?.data;
  const events = eventsResult.data?.data?.results ?? [];
  const eventsTotal = eventsResult.data?.data?.total ?? 0;
  const gatheredItems = gatheredItemsResult.data?.data?.results ?? [];
  const gatheredItemsTotal = gatheredItemsResult.data?.data?.total ?? 0;
  const gatheredRelationships = gatheredRelationshipsResult.data?.data?.results ?? [];
  const gatheredRelationshipsTotal = gatheredRelationshipsResult.data?.data?.total ?? 0;
  const importedItems = importedItemsResult.data?.data?.results ?? [];
  const importedItemsTotal = importedItemsResult.data?.data?.total ?? 0;
  const importedRelationships = importedRelationshipsResult.data?.data?.results ?? [];
  const importedRelationshipsTotal = importedRelationshipsResult.data?.data?.total ?? 0;
  const accountDetails = accountDetailsResult.data ?? [];
  const accountDetailsMap = keyBy(accountDetails, "id");
  const configAccountList = (upgradeResult.data?.data?.config_json?.account_list ??
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    []) as any[];

  const combinedAccountDetails = compact(
    configAccountList.map(accountConfig => {
      const accountDetails = accountDetailsMap[accountConfig.to_account_id];

      console.log(accountConfig);

      return {
        config: accountConfig,
        details: accountDetails,
      };
    })
  );

  const workflowId = upgrade?.id ? `V1BatchAccountUpgraderWorkflow-${upgrade.id}` : null;

  if (upgradeResult.isLoading) {
    return <div>Loading...</div>;
  }

  if (upgradeResult.isError) {
    return <div>Error loading batch account upgrade: {String(upgradeResult.error)}</div>;
  }

  if (!upgrade) {
    return <div>Batch account upgrade not found</div>;
  }

  return (
    <Container>
      <Flex justify="between" align="center" mb="4">
        <Heading size="6">Batch Account Upgrade</Heading>
        <Button variant="soft" onClick={() => navigate(atlasRoutes.batchAccountUpgrades({}).path)}>
          Back to List
        </Button>
      </Flex>

      <InfoCard>
        <CardHeader>
          <Heading size="4">{upgrade.id}</Heading>
          <Text as="p" size="2" color="gray">
            ID: {upgrade.id}
          </Text>
        </CardHeader>
        <CardContent>
          <InfoItem>
            <Text weight="bold">Started At:</Text>
            <Text>
              Temporal Cloud Link:{" "}
              {workflowId ? (
                <Link
                  href={`https://cloud.temporal.io/namespaces/prod-backend-async-worker.rqp4d/workflows/${workflowId}`}
                  target="_blank"
                >
                  {workflowId}
                </Link>
              ) : (
                "N/A"
              )}
            </Text>
          </InfoItem>
          <InfoItem>
            <Text weight="bold">Started At:</Text>
            <Text>
              {upgrade.started_at
                ? DateTime.fromISO(upgrade.started_at).toLocaleString(DateTime.DATETIME_FULL)
                : "N/A"}
            </Text>
          </InfoItem>
          <InfoItem>
            <Text weight="bold">Ended At:</Text>
            <Text>
              {upgrade.ended_at
                ? DateTime.fromISO(upgrade.ended_at).toLocaleString(DateTime.DATETIME_FULL)
                : "N/A"}
            </Text>
          </InfoItem>
          <InfoItem>
            <Text weight="bold">Created At:</Text>
            <Text>
              {DateTime.fromISO(upgrade.created_at).toLocaleString(DateTime.DATETIME_FULL)}
            </Text>
          </InfoItem>
          <InfoItem>
            <Text weight="bold">Updated At:</Text>
            <Text>
              {DateTime.fromISO(upgrade.updated_at).toLocaleString(DateTime.DATETIME_FULL)}
            </Text>
          </InfoItem>
        </CardContent>
      </InfoCard>

      <Tabs.Root defaultValue="targetAccounts">
        <Tabs.List>
          <Tabs.Trigger value="targetAccounts">
            Target Accounts ({targetAccountIds.length})
          </Tabs.Trigger>
          <Tabs.Trigger value="gatheredItems">Gathered Items ({gatheredItemsTotal})</Tabs.Trigger>
          <Tabs.Trigger value="gatheredRelationships">
            Gathered Relationships ({gatheredRelationshipsTotal})
          </Tabs.Trigger>
          <Tabs.Trigger value="importedItems">Imported Items ({importedItemsTotal})</Tabs.Trigger>
          <Tabs.Trigger value="importedRelationships">
            Imported Relationships ({importedRelationshipsTotal})
          </Tabs.Trigger>
          <Tabs.Trigger value="events">Events ({eventsTotal})</Tabs.Trigger>
        </Tabs.List>

        <Box pt="4">
          <Tabs.Content value="targetAccounts">
            {accountDetailsResult.isLoading ? (
              <div>Loading account details...</div>
            ) : accountDetailsResult.isError ? (
              <Box p="4">
                <Text color="red">
                  Error fetching account details: {String(accountDetailsResult.error)}
                </Text>
              </Box>
            ) : accountDetails.length > 0 ? (
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>From Firestore ID</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Display Name</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Account ID</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {combinedAccountDetails.map(({ config, details }) => (
                    <Table.Row key={config.to_account_id}>
                      <Table.Cell>{config.from_firestore_user_id}</Table.Cell>
                      <Table.Cell>
                        {truncate(details?.profile_display_name, { length: 20 })}
                      </Table.Cell>
                      <Table.Cell>{details?.profile_email_address}</Table.Cell>
                      <Table.Cell>{details?.id}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            ) : (
              <Box p="4">
                <Text>No target accounts found</Text>
              </Box>
            )}
          </Tabs.Content>

          <Tabs.Content value="gatheredItems">
            {gatheredItemsResult.isLoading ? (
              <div>Loading gathered items...</div>
            ) : gatheredItems.length > 0 ? (
              <>
                <Table.Root>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Item Type</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Item ID</Table.ColumnHeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {gatheredItems.map(item => (
                      <Table.Row key={item.id}>
                        <Table.Cell>{item.v1_item_kind}</Table.Cell>
                        <Table.Cell>{item.v1_item_id}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
                {gatheredItems.length < gatheredItemsTotal && (
                  <Box p="3">
                    <Text size="2" color="gray">
                      +{gatheredItemsTotal - gatheredItems.length} more items
                    </Text>
                  </Box>
                )}
              </>
            ) : (
              <Box p="4">
                <Text>No gathered items found</Text>
              </Box>
            )}
          </Tabs.Content>

          <Tabs.Content value="gatheredRelationships">
            {gatheredRelationshipsResult.isLoading ? (
              <div>Loading gathered relationships...</div>
            ) : gatheredRelationships.length > 0 ? (
              <>
                <Table.Root>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Relationship Type</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Source ID</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Source Kind</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Target ID</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Target Kind</Table.ColumnHeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {gatheredRelationships.map(relationship => (
                      <Table.Row key={relationship.id}>
                        <Table.Cell>{relationship.v1_relationship_kind}</Table.Cell>
                        <Table.Cell>{relationship.v1_item_id}</Table.Cell>
                        <Table.Cell>{relationship.v1_item_kind}</Table.Cell>
                        <Table.Cell>{relationship.linked_v1_item_id}</Table.Cell>
                        <Table.Cell>{relationship.linked_v1_item_kind}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
                {gatheredRelationships.length < gatheredRelationshipsTotal && (
                  <Box p="3">
                    <Text size="2" color="gray">
                      +{gatheredRelationshipsTotal - gatheredRelationships.length} more
                      relationships
                    </Text>
                  </Box>
                )}
              </>
            ) : (
              <Box p="4">
                <Text>No gathered relationships found</Text>
              </Box>
            )}
          </Tabs.Content>

          <Tabs.Content value="importedItems">
            {importedItemsResult.isLoading ? (
              <div>Loading imported items...</div>
            ) : importedItems.length > 0 ? (
              <>
                <Table.Root>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Item Type</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Imported Item ID</Table.ColumnHeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {importedItems.map(item => (
                      <Table.Row key={item.id}>
                        <Table.Cell>{item.imported_item_kind}</Table.Cell>
                        <Table.Cell>{item.imported_item_id}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
                {importedItems.length < importedItemsTotal && (
                  <Box p="3">
                    <Text size="2" color="gray">
                      +{importedItemsTotal - importedItems.length} more items
                    </Text>
                  </Box>
                )}
              </>
            ) : (
              <Box p="4">
                <Text>No imported items found</Text>
              </Box>
            )}
          </Tabs.Content>

          <Tabs.Content value="importedRelationships">
            {importedRelationshipsResult.isLoading ? (
              <div>Loading imported relationships...</div>
            ) : importedRelationships.length > 0 ? (
              <>
                <Table.Root>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Relationship Type</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Source Item</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Target Item</Table.ColumnHeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {importedRelationships.map(relationship => (
                      <Table.Row key={relationship.id}>
                        <Table.Cell>{relationship.imported_relationship_kind}</Table.Cell>
                        <Table.Cell>{relationship.imported_item_id}</Table.Cell>
                        <Table.Cell>{relationship.linked_imported_item_id}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
                {importedRelationships.length < importedRelationshipsTotal && (
                  <Box p="3">
                    <Text size="2" color="gray">
                      +{importedRelationshipsTotal - importedRelationships.length} more
                      relationships
                    </Text>
                  </Box>
                )}
              </>
            ) : (
              <Box p="4">
                <Text>No imported relationships found</Text>
              </Box>
            )}
          </Tabs.Content>

          <Tabs.Content value="events">
            {eventsResult.isLoading ? (
              <div>Loading events...</div>
            ) : events.length > 0 ? (
              <>
                <Table.Root>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Event Type</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Created At</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Event Data</Table.ColumnHeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {events.map(event => (
                      <Table.Row key={event.id}>
                        <Table.Cell>{event.kind}</Table.Cell>
                        <Table.Cell>
                          {DateTime.fromISO(event.created_at).toLocaleString(
                            DateTime.DATETIME_FULL
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <pre>{JSON.stringify(event, null, 2)}</pre>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
                {events.length < eventsTotal && (
                  <Box p="3">
                    <Text size="2" color="gray">
                      +{eventsTotal - events.length} more events
                    </Text>
                  </Box>
                )}
              </>
            ) : (
              <Box p="4">
                <Text>No events found</Text>
              </Box>
            )}
          </Tabs.Content>
        </Box>
      </Tabs.Root>
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
`;

const InfoCard = styled(Card)`
  margin-bottom: 24px;
`;

const CardHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid var(--gray-a4);
`;

const CardContent = styled.div`
  padding: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
`;
